import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React, {
  useEffect,
  useState,
  Suspense,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import envConfig from "../../../envConfig";
import appLogHelper from "../../API/AppLog/appLogHelper";
import Files from "../../API/Files";
import trainingAPI from "../../API/Training/trainingAPI";
import useFileSelector from "../../Hooks/useFileSelector";
import usePath from "../../Hooks/usePath";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory } from "../../Router";
import DocumentsScreen from "./DocumentsScreen";
import { initialEditorValue } from "../TestExample/commonUtils";
import downloader from "../Common/downloader";
import Constants from "../../Constants";

const DEFAULT_DOC_TYPE = Constants.DOCUMENT_TYPES[0]?.key;
const ALLOWED_DOC_TYPES = Constants.DOCUMENT_TYPES.map((u) => u.key);
const routePrefixForType = (type) =>
  type === "generativeTraining" ? "gn" : type === "rlTask" ? "rl" : "cn";

const SECTION_TO_TYPE = {
  cn: "conversationalTraining",
  gn: "generativeTraining",
  rl: "rlTask",
};

const DocumentsScreenContainer = ({ navigation }) => {
  const { t } = useTranslation();
  const documentTypes = Constants.DOCUMENT_TYPES;
  const { getActiveWorkspace, getAppLogs, getWorkspaceEntities } =
    useWorkspaces();

  const {
    data: createAPIResult,
    error: createAPIError,
    loading: createAPIInProgress,
    apiRequest: createAPIRequest,
    lastUpdate: lastCreationTime,
  } = useAPI(trainingAPI.postTrainingData, envConfig);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const {
    handleFileSelect: handleFileSelectGer_,
    filePath: filePathGeneric,
    handleImports: handleImportsGeneric,
    removeFile: removeFileGeneric,
    isFileUploadInProgress,
    batchUploadStatus,
  } = useFileSelector({
    _importType: "imported-training-data",
    _importFile: "gen_ai_training",
    contentType: "gen_ai_training",
    additionalFilenameParams: ``,
    _appLogImportType: "documentImport",
    _appLogImportEvent: "bulkImportGenAi file import",
    showSuccessOnUpload: false,
    flat: false,
    createDocument: true,
    uploadOnSelection: true,
    showPreview: true,
    allowMultiple: true,
    onShowPreview: () => {},
    onDocumentsCreated: (files) => {
      loadTrainingData();
      setIsImportWizardOpen(false);
    },
    onDocumentCreated: (file) => {
      loadTrainingData();
      setIsImportWizardOpen(false);
    },
  });
  const history = useHistory();
  const { params } = usePath();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [selectedItems, setSelectedItems] = useState([]);
  const [excludeValidation, setExcludeValidation] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [topicsForImport, setSelectedTopicsForImport] = useState();
  const [importScope, setImportScope] = useState("training");
  const [typeFilter, setTypeFilter] = useState(
    SECTION_TO_TYPE[params?.sectionId] || "all",
  );
  const [hasAnyData, setHasAnyData] = useState(false);
  const [selectedImportType, setSelectedImportType] = useState(
    SECTION_TO_TYPE[params?.sectionId] || DEFAULT_DOC_TYPE,
  );
  const [items, setItems] = useState(state.aiEntitiesConfig);

  const { user } = AppUser.useUser();
  const [NERs, setNERs] = useState(
    state?.aiEntitiesConfig?.filter((u) => u.type === "ner") || [],
  );
  const [Intents, setIntents] = useState(
    state?.aiEntitiesConfig?.filter((u) => u.type === "intent") || [],
  );
  const [textString, setTextString] = useState("");
  const [documentIdString, setDocumentIdString] = useState("");
  const searchTimeoutRef = useRef(null);
  const onTrainingDataChange = (data) => {};
  const AppToasty = AppToast.useToast();
  const {
    error: trainingAPIError,
    loading: trainingAPIInProgress,
    apiRequest: fetchTraining,
  } = useAPI(trainingAPI.getDocumentsByWorkspace, null, onTrainingDataChange);

  const {
    error: trainingDeletionError,
    loading: trainingDeletionInProgress,
    apiRequest: deleteTrainingData,
  } = useAPI(trainingAPI.deleteTrainingData);

  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);

  const { loading: UploadInProgress, apiRequest: S3uploadRequest } = useAPI(
    Files.bulkImportFileUpload,
    envConfig,
  );

  const typesToFetch = () =>
    typeFilter !== "all" ? [typeFilter] : ALLOWED_DOC_TYPES;

  const fetchAllPages = async (params) => {
    const firstPage = await fetchTraining({ ...params, page: 1 });
    const totalPages = firstPage?.totalPages || 1;
    let data = firstPage?.data || [];
    if (totalPages > 1) {
      const remainingPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          fetchTraining({ ...params, page: i + 2 }),
        ),
      );
      remainingPages?.forEach((r) => {
        data = [...data, ...(r?.data || [])];
      });
    }
    return { ...firstPage, data, page: 1 };
  };

  const fetchDocuments = async (params) => {
    if (!state.activeWorkspaceID) return;
    const baseParams = {
      workspaceID: state.activeWorkspaceID,
      page: params.page,
      intents: (params.selectedItems || selectedItems)
        .filter((i) => i.category === "intent")
        .map((i) => i.label),
      entities: (params.selectedItems || selectedItems)
        .filter((i) => i.category === "ner")
        .map((i) => i.label),
      text: params.text,
      topics: (params.selectedItems || selectedItems)
        .filter((i) => i.category === "topic")
        .map((i) => i.id),
      documentIdString: params.documentIdString,
      scope: params.scope,
    };
    const types = params.type ? [params.type] : typesToFetch();
    try {
      const results = await Promise.all(
        types.map((type) => fetchAllPages({ ...baseParams, type })),
      );
      const merged = results.reduce(
        (acc, r) => ({
          data: [...(acc.data || []), ...(r?.data || [])],
          totalResults: (acc.totalResults || 0) + (r?.totalResults || 0),
          page: params.page,
          totalPages: Math.max(acc.totalPages || 0, r?.totalPages || 0),
        }),
        { data: [], totalResults: 0, page: params.page, totalPages: 0 },
      );
      dispatch("SET_TRAINING_DATA", merged);
      return merged;
    } catch (error) {
      console.error("Error fetching training documents", error);
    }
  };

  const refreshHasAnyData = async () => {
    if (!state.activeWorkspaceID) return;
    try {
      const counts = await Promise.all(
        ALLOWED_DOC_TYPES.flatMap((type) =>
          ["training", "validation"].map((scope) =>
            fetchTraining({
              workspaceID: state.activeWorkspaceID,
              page: 1,
              type,
              countOnly: true,
              scope,
            }),
          ),
        ),
      );
      setHasAnyData(counts.some((r) => (r?.totalResults || 0) > 0));
    } catch (error) {
      console.error("Error checking training data existence", error);
    }
  };

  const handleDocumentCreation = async (type) => {
    const createType =
      type || (typeFilter !== "all" ? typeFilter : DEFAULT_DOC_TYPE);
    try {
      const trainingDataPostResult = await createAPIRequest({
        data: [
          {
            workspaceid: getActiveWorkspace()?.id,
            document: JSON.stringify(initialEditorValue),
            rawtext: "",
            trainingData: {},
            userID: user?.id,
            type: createType,
            status: "published",
            topics: [],
            scope: "training",
          },
        ],
      });
      AppToasty.show(t("toasts.trainingDocumentCreated"), {
        placement: "top",
        type: "success",
      });
      setTimeout(() => {
        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/documents/${routePrefixForType(
            createType,
          )}/${trainingDataPostResult?.data?.[0]?.id}`,
        );
      }, [1000]);
    } catch (error) {
      console.error("Error while creating training data", error);
      AppToasty.show(t("TrainingDataCreationFailedCaption"), {
        placement: "top",
        type: "danger",
      });
    }
  };
  const onItemPress = (item) => {
    setSelectedItems(item);
  };
  const handleExcludeValidation = (v) => {
    setExcludeValidation(v);
    debouncedSearch({ excludeValidation: v });
  };
  const handleDocumentSelection = async ({ id }) => {
    const obj = state.trainingData?.data?.find((u) => u?.id == id);
    const rawType = obj?.type;
    if (obj?.external_path)
      try {
        AppToasty.show(t("training.downloadStarted"), {
          placement: "bottom",
          type: "success",
        });
        const result = await S3downloadUrlRequest({
          workspaceid: getActiveWorkspace()?.id,
          logId: null,
          type: obj?.external_path?.split(".").pop(),
          filename: obj?.external_path.replace(
            `s3://files-to-import-${envConfig.REACT_APP_REGION}-${envConfig.REACT_APP_ENV}/`,
            "",
          ),
          context: "pdf",
        });

        downloader.download_files(
          [
            {
              download: result.url,
              filename: obj?.title,
            },
          ],
          true,
        );
      } catch (error) {
        console.error("Training data download error", error);
        AppToasty.show(t("training.downloadFailed"), {
          placement: "bottom",
          type: "danger",
        });
      }
    else {
      if (rawType === "nlpTraining")
        history.push(`/workspaces/${state.activeWorkspaceID}/nlp/${id}`);
      else if (rawType === "readComprehensionTraining")
        history.push(
          `/workspaces/${state.activeWorkspaceID}/documents/cn/${id}`,
        );
      else if (rawType === "generativeTraining")
        history.push(
          `/workspaces/${state.activeWorkspaceID}/documents/gn/${id}`,
        );
      else if (rawType === "rlTask")
        history.push(
          `/workspaces/${state.activeWorkspaceID}/documents/rl/${id}`,
        );
      else if (rawType === "conversationalTraining")
        history.push(
          `/workspaces/${state.activeWorkspaceID}/documents/cn/${id}`,
        );
    }
  };
  const handleChangeText = (text) => {
    setTextString(text);
    debouncedSearch({ textString: text });
  };
  const handleChangeDocumentIdString = (text) => {
    setDocumentIdString(text);
    debouncedSearch({ documentIdString: text });
  };
  const handleDocumentDeletion = async (document) => {
    try {
      await deleteTrainingData(document.id, state.activeWorkspaceID);
      dispatch(
        "SET_TRAINING_DATA",
        state.trainingData
          ? {
              ...state.trainingData,
              data: state.trainingData?.data.filter((i) => i.id !== document.id),
              totalResults: Math.max(
                0,
                (state.trainingData?.totalResults || 0) - 1,
              ),
            }
          : state.trainingData,
      );
      getDocuments();
      refreshHasAnyData();
      AppToasty.show(t("DocumentDeletionCaption"), { type: "success" });
    } catch (error) {
      AppToasty.show(t("DocumentDeletionFailedCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDocumentDeletion = async (documents) => {
    const deletable = (documents || []).filter((d) => {
      const raw = state.trainingData?.data?.find((u) => u.id == d.id);
      return raw?.source !== "api";
    });
    if (deletable.length === 0) return;
    let failures = 0;
    try {
      const results = await Promise.allSettled(
        deletable.map((d) =>
          deleteTrainingData(d.id, state.activeWorkspaceID),
        ),
      );
      failures = results.filter((r) => r.status === "rejected").length;
    } catch (error) {
      AppToasty.show(t("documents.bulkDeleteFailedCaption"), {
        type: "danger",
      });
      return;
    }
    const deletedIds = new Set(deletable.map((d) => String(d.id)));
    dispatch(
      "SET_TRAINING_DATA",
      state.trainingData
        ? {
            ...state.trainingData,
            data: state.trainingData?.data?.filter(
              (i) => !deletedIds.has(String(i.id)),
            ),
            totalResults: Math.max(
              0,
              (state.trainingData?.totalResults || 0) - deletedIds.size,
            ),
          }
        : state.trainingData,
    );
    getDocuments();
    refreshHasAnyData();
    if (failures > 0) {
      AppToasty.show(
        t("documents.bulkDeletePartialFailedCaption", { count: failures }),
        { type: "danger" },
      );
    } else {
      AppToasty.show(t("documents.bulkDeleteCaption"), { type: "success" });
    }
  };
  const onTopicCreated = () => {
    setItems(
      getWorkspaceEntities("topic")?.map((topic) => {
        topic.name = topic?.label;
        topic.category = topic.type;
        return topic;
      }),
    );
  };
  const getDocuments = (page) => {
    fetchDocuments({
      page: page || currentPage,
      selectedItems,
      text: textString,
      documentIdString,
      scope: excludeValidation ? "training" : undefined,
    });
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
    getDocuments();
  };

  const generateItems = (selectedItems) => {
    let newItems = [...items];
    if (newItems)
      for (let i = 0; i < newItems.length; i++) {
        let sItem = newItems[i];
        const sItemIndex = selectedItems.map((i) => i.id).indexOf(sItem.id);
        if (sItemIndex > -1) sItem.selected = true;
        else sItem.selected = false;
        newItems[sItemIndex] = sItem;
      }
    return newItems;
  };
  const onDeleteSelectedItem = (item) => {
    const indexOfItemInSelectedItems = selectedItems
      .map((i) => i.id)
      .indexOf(item.id);
    selectedItems.splice(indexOfItemInSelectedItems, 1);
    setSelectedItems([...selectedItems]);
  };
  useEffect(() => {
    if (selectedItems?.length >= 0) {
      setItems(generateItems(selectedItems));
      debouncedSearch({ selectedItems });
    }
  }, [selectedItems]);
  useEffect(() => {
    setItems(state.aiEntitiesConfig);
    setNERs(state?.aiEntitiesConfig?.filter((u) => u.type === "ner") || []);
    setIntents(
      state?.aiEntitiesConfig?.filter((u) => u.type === "intent") || [],
    );
  }, [JSON.stringify(state.aiEntitiesConfig)]);
  useEffect(() => {
    if (
      state.redirectToDoc &&
      state.redirectToDoc.type === "readComprehensionTraining"
    ) {
      history.push(
        `/workspaces/${state.activeWorkspaceID}/answerer/${state.redirectToDoc.id}`,
      );
      dispatch("SET_REDIRECT_TO_DOC", null);
    }
  }, [state.redirectToDoc]);
  const loadTrainingData = async () => {
    await getDocuments(1);
  };
  const debouncedSearch = useCallback(
    (searchParams = {}) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchDocuments({
          page: 1,
          selectedItems: searchParams.selectedItems || selectedItems,
          text:
            searchParams.textString !== undefined
              ? searchParams.textString
              : textString,
          documentIdString:
            searchParams.documentIdString !== undefined
              ? searchParams.documentIdString
              : documentIdString,
          type: searchParams.type,
          scope: (
            searchParams.excludeValidation !== undefined
              ? searchParams.excludeValidation
              : excludeValidation
          )
            ? "training"
            : undefined,
        });
      }, 300);
    },
    [
      state.activeWorkspaceID,
      fetchTraining,
      selectedItems,
      textString,
      typeFilter,
      documentIdString,
      excludeValidation,
    ],
  );

  const handleSearch = () => {
    setCurrentPage(1);
    getDocuments(1);
  };
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);
  useEffect(() => {
    if (state?.activeWorkspaceID) {
      loadTrainingData();
      refreshHasAnyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.activeWorkspaceID]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);
  const stopImportProcess = () => {
    const appLog = getAppLogs("import", "dispatched")[0];
    if (appLog) {
      appLogHelper.deleteAppLog(appLog?.id);
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <DocumentsScreen
        documentIdString={documentIdString}
        handleChangeDocumentIdString={handleChangeDocumentIdString}
        handleExcludeValidation={handleExcludeValidation}
        excludeValidation={excludeValidation}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        trainingData={state.trainingData}
        hasAnyData={hasAnyData}
        requestInProgress={
          trainingAPIInProgress ||
          trainingDeletionInProgress ||
          state.trainingData == null
        }
        handleDocumentCreation={handleDocumentCreation}
        createAPIInProgress={createAPIInProgress}
        importProgress={`${AppUtilities.math.roundTo(
          (0 / 1) * 100,
          2,
        )}%`}
        isImportEnabled={
          state?.appLogsReady && getAppLogs("import")?.length < 1
        }
        isImportInProgress={getAppLogs("import")?.length > 0}
        isImportStarted={getAppLogs("import", false, "dispatched")?.length > 0}
        onItemPress={onItemPress}
        stopImportProcess={stopImportProcess}
        onDeleteSelectedItem={onDeleteSelectedItem}
        selectedItems={selectedItems}
        items={items}
        NERStyles={NERs}
        IntentStyles={Intents}
        textString={textString}
        handleChangeText={handleChangeText}
        handleDocumentSelection={handleDocumentSelection}
        handleDocumentDeletion={handleDocumentDeletion}
        handleBulkDocumentDeletion={handleBulkDocumentDeletion}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        documentTypes={documentTypes}
        isImportWizardOpen={isImportWizardOpen}
        setIsImportWizardOpen={setIsImportWizardOpen}
        selectedImportType={selectedImportType}
        setSelectedImportType={setSelectedImportType}
        handleFileSelect={handleFileSelectGer_}
        handleImportsGeneric={() =>
          handleImportsGeneric(
            selectedImportType,
            null,
            "draft",
            topicsForImport,
            "generic",
            "gen_ai_training",
            importScope,
            "dataImporter",
          )
        }
        isFileUploadInProgress={isFileUploadInProgress}
        batchUploadStatus={batchUploadStatus}
        filePathGeneric={filePathGeneric}
        removeFile={removeFileGeneric}
        topicsForImport={topicsForImport}
        setSelectedTopicsForImport={setSelectedTopicsForImport}
        importScope={importScope}
        setImportScope={setImportScope}
        onTopicCreated={onTopicCreated}
      />
    </Suspense>
  );
};

export default DocumentsScreenContainer;
